import { View, Text, Pressable } from 'react-native';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import { useRouter } from 'expo-router';
import Toast from 'react-native-toast-message';
import Unicon from '@/components/General/Unicon';
import FeatureIcon, { getRouteFeature } from '@/components/General/FeatureIcon';
import useShortcutAccess from '@/utils/hooks/useShortcutAccess';

const SHORTCUT_ICONS = {
    '/people': 'people-outline',
    '/events': 'calendar-outline',
    '/tasks': 'checkbox-outline',
    '/smallgroups': 'book-outline',
    '/connect': 'chatbubbles-outline',
    '/contributions': 'cash-outline',
    '/analytics': 'bar-chart-outline',
    '/library': 'folder-outline',
    '/music': 'musical-notes-outline',
    '/roles': 'shield-outline',
    '/permissions': 'key-outline',
    '/registrations': 'person-add-outline',
    '/customfields': 'options-outline',
    'default': 'link-outline'
};

const MODULE_COLOR_KEY = {
    'People': 'people',
    'Events': 'events',
    'Tasks': 'tasks',
    'Roles': 'admin',
    'Training': 'training',
    'Automations': 'paths',
    'Paths': 'paths', // legacy stored module name
    'Follow Up': 'paths', // rides the shared 'paths' module color
    'Small Groups': 'groups',
    'Connect': 'connect',
    'Giving': 'giving',
    'Analytics': 'analytics',
    'Admin': 'admin',
    'Other': 'primary',
};

const inferModuleFromScreen = (screen) => {
    if (!screen) return 'Other';
    const seg = screen.split('/')[1];
    if (seg === 'people') return 'People';
    if (seg === 'events') return 'Events';
    if (seg === 'tasks') return 'Tasks';
    if (seg === 'roles' || seg === 'permissions') return 'Roles';
    if (seg === 'training') return 'Training';
    if (seg === 'automations' || seg === 'paths') return 'Automations';
    if (seg === 'follow-up') return 'Follow Up';
    if (seg === 'smallgroups') return 'Small Groups';
    if (seg === 'connect') return 'Connect';
    if (seg === 'contributions') return 'Giving';
    if (seg === 'analytics') return 'Analytics';
    if (seg === 'customfields' || seg === 'registrations') return 'Admin';
    return 'Other';
};

const getIconForScreen = (screen, customIcon) => {
    if (customIcon) return customIcon;
    const baseRoute = '/' + screen?.split('/')[1];
    return SHORTCUT_ICONS[baseRoute] || SHORTCUT_ICONS['default'];
};

const ShortcutCard = ({ shortcut, onLongPress }) => {
    const router = useRouter();
    const { theme: { colors } } = useUnistyles();
    const canAccess = useShortcutAccess();
    const label = shortcut.parameters?.label || shortcut.screen?.split('/').pop() || 'Shortcut';
    const icon = getIconForScreen(shortcut.screen, shortcut.parameters?.icon);

    const mod = shortcut.parameters?.module || inferModuleFromScreen(shortcut.screen);
    const colorKey = MODULE_COLOR_KEY[mod] || 'primary';
    const moduleColor = colors[colorKey];
    const moduleContainer = colors[`${colorKey}Container`] || colors.primaryContainer;
    const feature = getRouteFeature(shortcut.screen);

    // Defense-in-depth: even if a stale card renders, never navigate to a
    // screen the user can't access.
    const handlePress = () => {
        if (!canAccess(shortcut.screen)) {
            Toast.show({ type: 'error', text2: "You don't have permission to open this" });
            return;
        }
        router.push({ pathname: shortcut.screen });
    };

    return (
        <Pressable
            style={styles.container}
            onPress={handlePress}
            onLongPress={onLongPress}
            delayLongPress={500}
        >
            <View style={[styles.iconBubble, { backgroundColor: feature ? 'transparent' : moduleContainer }]}>
                {feature ? <FeatureIcon feature={feature} /> :
                    <Unicon name={icon} size={18} style={{ color: moduleColor }} library={shortcut.parameters?.library || 'ionicon'} />}
            </View>
            <Text style={styles.label} numberOfLines={2}>{label}</Text>
        </Pressable>
    );
};

const styles = StyleSheet.create(({ colors, fonts, spacing, typography, shadows }) => ({
    container: {
        alignItems: 'center',
        justifyContent: 'center',
        padding: spacing.sm,
        borderRadius: 4,
        backgroundColor: colors.surface,
        width: 88,
        height: 88,
        margin: 3,
        boxShadow: shadows.none,
        _web: {
            transition: "all 0.15s ease",
            cursor: "pointer",
            _hover: { backgroundColor: colors.hover },
        },
    },
    iconBubble: {
        width: 34,
        height: 34,
        borderRadius: 10,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: spacing.xs,
    },
    label: {
        fontSize: typography.micro,
        fontFamily: fonts.heading,
        color: colors.text,
        textAlign: 'center',
        lineHeight: 13,
    },
}));

export default ShortcutCard;
